import { useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import { useGetAccountOpeningDescriptorQuery, useGetAccountTypeSelectorsQuery, useGetAccountTypesQuery } from '../../../store/integrations/integrations'
import { type AccountType, type AccountOpeningTypeSelector } from '../../../models/AccountOpening'
import { type AccountOpeningDescriptor } from '../../../models/AccountOpeningDescriptor'
import { unresolvedAxes } from '../domain/accountTypeCatalog'
import { descriptorIssues } from '../descriptor/descriptorIssues'

interface ResolvedDescriptor {
  descriptor: AccountOpeningDescriptor | undefined
  isDescriptorLoading: boolean
  descriptorFailed: boolean
}

const useAccountOpeningDescriptor = (accountId: string, connector: string): ResolvedDescriptor => {
  const { data, isLoading, isError } = useGetAccountOpeningDescriptorQuery({ accountId, connector }, { skip: !connector })
  const descriptor = data?.data

  useEffect(() => {
    if (!descriptor) {
      return
    }
    const issues = descriptorIssues(descriptor)
    if (issues.length > 0) {
      Sentry.captureException(new Error(`Account-opening descriptor (${descriptor.connector} v${descriptor.version}): ${issues.join('; ')}`))
    }
  }, [descriptor])

  return { descriptor, isDescriptorLoading: isLoading, descriptorFailed: isError }
}

const useCatalogAxisCheck = (descriptor: AccountOpeningDescriptor | undefined, selectors: AccountOpeningTypeSelector[], accountTypes: AccountType[]): void => {
  useEffect(() => {
    if (!descriptor || accountTypes.length === 0 || selectors.length === 0) {
      return
    }
    const unresolved = unresolvedAxes(accountTypes, selectors)
    if (unresolved.length > 0) {
      Sentry.captureException(
        new Error(
          `Account-opening descriptor (${descriptor.connector} v${descriptor.version}): ${descriptor.entities.accountTypeSelector} names fields no account type carries: ${unresolved.join(', ')}`,
        ),
      )
    }
  }, [descriptor, selectors, accountTypes])
}

export interface ResolvedCatalog extends Omit<ResolvedDescriptor, 'isDescriptorLoading'> {
  selectors: AccountOpeningTypeSelector[]
  accountTypes: AccountType[]
  isLoadingAccountTypes: boolean
  accountTypesFailed: boolean
  selectorsFailed: boolean
}

export const useAccountTypeCatalog = (accountId: string, connector: string, options?: { refetchOnMountOrArgChange?: boolean }): ResolvedCatalog => {
  const { descriptor, isDescriptorLoading, descriptorFailed } = useAccountOpeningDescriptor(accountId, connector)

  const {
    data: accountTypesResponse,
    isFetching,
    error,
  } = useGetAccountTypesQuery(
    { accountId, connector, entity: descriptor?.entities.accountType ?? '' },
    { skip: !descriptor, refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange },
  )
  const accountTypes: AccountType[] = useMemo(() => accountTypesResponse?.data ?? [], [accountTypesResponse])

  const {
    data: selectorsResponse,
    isFetching: isFetchingSelectors,
    error: selectorsError,
  } = useGetAccountTypeSelectorsQuery(
    { accountId, connector, entity: descriptor?.entities.accountTypeSelector ?? '' },
    { skip: !descriptor, refetchOnMountOrArgChange: options?.refetchOnMountOrArgChange },
  )
  const selectors: AccountOpeningTypeSelector[] = useMemo(() => selectorsResponse?.data ?? [], [selectorsResponse])

  useCatalogAxisCheck(descriptor, selectors, accountTypes)

  return {
    descriptor,
    descriptorFailed,
    selectors,
    accountTypes,
    isLoadingAccountTypes: !connector || isDescriptorLoading || isFetching || isFetchingSelectors,
    accountTypesFailed: !!error,
    selectorsFailed: !!selectorsError,
  }
}
